import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { EventSong, HitsterEvent, EventRound } from '../types';
import './Lingo.css';

const API_BASE = process.env.REACT_APP_API_URL || '';

interface LetterState {
  letter: string;
  status: 'hidden' | 'revealed' | 'correct' | 'wrong-position' | 'wrong';
}

interface LingoGuess {
  letters: LetterState[];
}

const Lingo: React.FC = () => {
  const navigate = useNavigate();
  const { eventId, roundId } = useParams<{ eventId: string; roundId: string }>();
  
  const [event, setEvent] = useState<HitsterEvent | null>(null);
  const [round, setRound] = useState<EventRound | null>(null);
  const [loading, setLoading] = useState(true);
  
  // Game state
  const [gameMode, setGameMode] = useState<'artist' | 'title'>('artist');
  const [currentSongIndex, setCurrentSongIndex] = useState(0);
  const [targetWord, setTargetWord] = useState('');
  const [guesses, setGuesses] = useState<LingoGuess[]>([]);
  const [currentGuess, setCurrentGuess] = useState('');
  const [gameStatus, setGameStatus] = useState<'playing' | 'won' | 'lost'>('playing');
  const [revealedLetters, setRevealedLetters] = useState<boolean[]>([]);
  const [hintsUsed, setHintsUsed] = useState(0);
  const [score, setScore] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);
  
  const maxGuesses = 5;

  // Load event and round data
  useEffect(() => {
    const loadData = async () => {
      if (!eventId) {
        setLoading(false);
        return;
      }
      
      try {
        const response = await fetch(`${API_BASE}/api/events/${eventId}`);
        if (response.ok) {
          const data = await response.json();
          setEvent(data);
          if (roundId) {
            const r = data.rounds.find((r: EventRound) => r.id === roundId);
            setRound(r || null);
          }
        }
      } catch (err) {
        console.error('Failed to load event:', err);
      }
      setLoading(false);
    };
    loadData();
  }, [eventId, roundId]);

  // Initialize game when round changes
  useEffect(() => {
    if (round && round.songs.length > 0) {
      startNewWord(0);
    }
  }, [round, gameMode]);

  const cleanWord = (word: string): string => {
    // Remove special characters, keep only letters and spaces
    return word.toUpperCase().replace(/[^A-Z\s]/g, '').trim();
  };

  const startNewWord = (songIndex: number) => {
    if (!round || songIndex >= round.songs.length) return;
    
    const song = round.songs[songIndex];
    const word = gameMode === 'artist' ? song.artist : song.title;
    const cleaned = cleanWord(word);
    
    if (cleaned.length === 0) {
      // Skip songs with no valid letters
      if (songIndex + 1 < round.songs.length) {
        startNewWord(songIndex + 1);
      }
      return;
    }
    
    setCurrentSongIndex(songIndex);
    setTargetWord(cleaned);
    setGuesses([]);
    setCurrentGuess('');
    setGameStatus('playing');
    setShowAnswer(false);
    setHintsUsed(0);
    
    // Reveal first letter as hint (like Lingo)
    const revealed = new Array(cleaned.length).fill(false);
    revealed[0] = true;
    setRevealedLetters(revealed);
  };

  const checkGuess = useCallback(() => {
    if (currentGuess.length !== targetWord.length || gameStatus !== 'playing') return;
    
    const guessUpper = currentGuess.toUpperCase();
    const letters: LetterState[] = [];
    const targetLetters = targetWord.split('');
    const usedIndices = new Set<number>();
    
    // First pass: mark correct positions
    for (let i = 0; i < guessUpper.length; i++) {
      if (guessUpper[i] === targetLetters[i]) {
        letters.push({ letter: guessUpper[i], status: 'correct' });
        usedIndices.add(i);
      } else {
        letters.push({ letter: guessUpper[i], status: 'wrong' });
      }
    }
    
    // Second pass: mark wrong positions
    for (let i = 0; i < letters.length; i++) {
      if (letters[i].status === 'correct') continue;
      
      for (let j = 0; j < targetLetters.length; j++) {
        if (!usedIndices.has(j) && letters[i].letter === targetLetters[j]) {
          letters[i].status = 'wrong-position';
          usedIndices.add(j);
          break;
        }
      }
    }
    
    const newGuesses = [...guesses, { letters }];
    setGuesses(newGuesses);
    setCurrentGuess('');
    
    // Check if won
    if (guessUpper === targetWord) {
      setGameStatus('won');
      const points = Math.max(100 - (newGuesses.length - 1) * 15 - hintsUsed * 10, 10);
      setScore(score + points);
    } else if (newGuesses.length >= maxGuesses) {
      setGameStatus('lost');
      setShowAnswer(true);
    }
  }, [currentGuess, targetWord, gameStatus, guesses, hintsUsed, score]);

  const revealHint = () => {
    if (gameStatus !== 'playing') return;
    
    // Find a hidden letter to reveal
    const hiddenIndices = revealedLetters
      .map((revealed, i) => (!revealed ? i : -1))
      .filter(i => i !== -1);
    
    if (hiddenIndices.length === 0) return;
    
    const randomIndex = hiddenIndices[Math.floor(Math.random() * hiddenIndices.length)];
    const newRevealed = [...revealedLetters];
    newRevealed[randomIndex] = true;
    setRevealedLetters(newRevealed);
    setHintsUsed(hintsUsed + 1);
  };

  const nextWord = () => {
    if (currentSongIndex + 1 < (round?.songs.length || 0)) {
      startNewWord(currentSongIndex + 1);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (gameStatus !== 'playing') return;
    
    if (e.key === 'Enter') {
      checkGuess();
    } else if (e.key === 'Backspace') {
      setCurrentGuess(currentGuess.slice(0, -1));
    } else if (/^[a-zA-Z\s]$/.test(e.key) && currentGuess.length < targetWord.length) {
      setCurrentGuess(currentGuess + e.key.toUpperCase());
    }
  };

  const handleVirtualKey = (key: string) => {
    if (gameStatus !== 'playing') return;
    
    if (key === 'ENTER') {
      checkGuess();
    } else if (key === 'DEL') {
      setCurrentGuess(currentGuess.slice(0, -1));
    } else if (currentGuess.length < targetWord.length) {
      setCurrentGuess(currentGuess + key);
    }
  };

  const currentSong = round?.songs[currentSongIndex];

  if (loading) {
    return <div className="lingo loading">Laden...</div>;
  }

  if (!eventId) {
    return (
      <div className="lingo">
        <div className="lingo-header">
          <button className="back-btn" onClick={() => navigate('/spotify-dashboard')}>
            ← Terug
          </button>
          <h1>🔤 Lingo</h1>
        </div>
        <div className="lingo-info">
          <p>Selecteer eerst een evenement in het dashboard.</p>
          <button onClick={() => navigate('/spotify-dashboard')}>Ga naar Dashboard</button>
        </div>
      </div>
    );
  }

  if (eventId && event && !roundId) {
    return (
      <div className="lingo">
        <div className="lingo-header">
          <button className="back-btn" onClick={() => navigate('/spotify-dashboard')}>
            ← Terug
          </button>
          <h1>🔤 Lingo - {event.name}</h1>
        </div>
        <div className="lingo-controls">
          <div className="round-selector">
            <h3>Selecteer een ronde:</h3>
            {event.rounds && event.rounds.length > 0 ? (
              <div className="round-list">
                {event.rounds.map((r: EventRound) => (
                  <button
                    key={r.id}
                    className="round-select-btn"
                    onClick={() => navigate(`/lingo/${eventId}/${r.id}`)}
                  >
                    {r.name} ({r.songs?.length || 0} nummers)
                  </button>
                ))}
              </div>
            ) : (
              <p>Nog geen rondes aangemaakt.</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (!round || round.songs.length === 0) {
    return (
      <div className="lingo">
        <div className="lingo-header">
          <button className="back-btn" onClick={() => navigate('/spotify-dashboard')}>
            ← Terug
          </button>
          <h1>🔤 Lingo</h1>
        </div>
        <div className="lingo-info">
          <p>Geen nummers gevonden in deze ronde.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="lingo" tabIndex={0} onKeyDown={handleKeyPress}>
      <div className="lingo-header">
        <button className="back-btn" onClick={() => navigate('/spotify-dashboard')}>
          ← Terug
        </button>
        <h1>🔤 Lingo - {event?.name}</h1>
        <div className="score-display">Score: {score}</div>
      </div>

      <div className="lingo-game">
        {/* Mode selector */}
        <div className="mode-selector">
          <button 
            className={`mode-btn ${gameMode === 'artist' ? 'active' : ''}`}
            onClick={() => setGameMode('artist')}
          >
            🎤 Artiest raden
          </button>
          <button 
            className={`mode-btn ${gameMode === 'title' ? 'active' : ''}`}
            onClick={() => setGameMode('title')}
          >
            🎵 Titel raden
          </button>
        </div>

        {/* Progress */}
        <div className="progress-info">
          <span>Nummer {currentSongIndex + 1} van {round.songs.length}</span>
          {currentSong && (
            <span className="hint-text">
              {gameMode === 'artist' ? `Titel: ${currentSong.title}` : `Artiest: ${currentSong.artist}`}
              {currentSong.year && ` (${currentSong.year})`}
            </span>
          )}
        </div>

        {/* Game board */}
        <div className="lingo-board">
          {/* Starting word with revealed letters */}
          <div className="word-row starting-row">
            {targetWord.split('').map((letter, i) => (
              <div key={i} className={`letter-cell ${revealedLetters[i] ? 'revealed' : 'hidden'}`}>
                {revealedLetters[i] ? letter : ''}
              </div>
            ))}
          </div>

          {/* Previous guesses */}
          {guesses.map((guess, rowIndex) => (
            <div key={rowIndex} className="word-row">
              {guess.letters.map((letter, i) => (
                <div key={i} className={`letter-cell ${letter.status}`}>
                  {letter.letter}
                </div>
              ))}
            </div>
          ))}

          {/* Current guess */}
          {gameStatus === 'playing' && guesses.length < maxGuesses && (
            <div className="word-row current">
              {targetWord.split('').map((_, i) => (
                <div key={i} className={`letter-cell ${i < currentGuess.length ? 'filled' : 'empty'}`}>
                  {currentGuess[i] || ''}
                </div>
              ))}
            </div>
          )}

          {/* Empty rows */}
          {Array.from({ length: Math.max(0, maxGuesses - guesses.length - (gameStatus === 'playing' ? 1 : 0)) }).map((_, rowIndex) => (
            <div key={`empty-${rowIndex}`} className="word-row empty-row">
              {targetWord.split('').map((_, i) => (
                <div key={i} className="letter-cell empty"></div>
              ))}
            </div>
          ))}
        </div>

        {/* Game result */}
        {gameStatus === 'won' && (
          <div className="game-result won">
            <h2>🎉 Goed geraden!</h2>
            <p>Je hebt het in {guesses.length} {guesses.length === 1 ? 'poging' : 'pogingen'} geraden!</p>
          </div>
        )}

        {gameStatus === 'lost' && (
          <div className="game-result lost">
            <h2>😔 Helaas!</h2>
            <p>Het antwoord was: <strong>{targetWord}</strong></p>
          </div>
        )}

        {/* Controls */}
        <div className="game-controls">
          {gameStatus === 'playing' && (
            <>
              <button className="hint-btn" onClick={revealHint}>
                💡 Hint ({revealedLetters.filter(r => r).length - 1} gebruikt)
              </button>
              <button className="give-up-btn" onClick={() => { setGameStatus('lost'); setShowAnswer(true); }}>
                ⏭️ Overslaan
              </button>
            </>
          )}
          
          {gameStatus !== 'playing' && currentSongIndex + 1 < round.songs.length && (
            <button className="next-btn" onClick={nextWord}>
              ➡️ Volgende woord
            </button>
          )}
          
          {gameStatus !== 'playing' && currentSongIndex + 1 >= round.songs.length && (
            <div className="final-score">
              <h2>🏆 Ronde voltooid!</h2>
              <p>Eindscore: {score} punten</p>
              <button onClick={() => startNewWord(0)}>🔄 Opnieuw spelen</button>
            </div>
          )}
        </div>

        {/* Virtual keyboard */}
        {gameStatus === 'playing' && (
          <div className="virtual-keyboard">
            {['QWERTYUIOP', 'ASDFGHJKL', 'ZXCVBNM'].map((row, rowIndex) => (
              <div key={rowIndex} className="keyboard-row">
                {rowIndex === 2 && (
                  <button className="key-btn wide" onClick={() => handleVirtualKey('ENTER')}>
                    ✓
                  </button>
                )}
                {row.split('').map(key => (
                  <button key={key} className="key-btn" onClick={() => handleVirtualKey(key)}>
                    {key}
                  </button>
                ))}
                {rowIndex === 2 && (
                  <button className="key-btn wide" onClick={() => handleVirtualKey('DEL')}>
                    ⌫
                  </button>
                )}
              </div>
            ))}
            <div className="keyboard-row">
              <button className="key-btn space" onClick={() => handleVirtualKey(' ')}>
                SPATIE
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Lingo;
